import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  List,
  NumberField,
  ReferenceField,
  SelectInput,
  ShowButton,
  SimpleList,
  TextField,
  TopToolbar,
} from "react-admin";
import SecuredComponent from "server/security/SecuredComponent";
import { checkPermission } from "server/security/utils";
import { administrationRequests } from "api/administrationApi";
import { doctoralRequests } from "api/doctoralApi";
import { DoctoralSeatStatus, MOBILE_BREAKPOINT } from "utils/constants";
import { Chip, useMediaQuery } from "@mui/material";
import { EmptyList } from "server/components";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { DoctoralSeatStatusChoices } from "utils/choices";
import { doctoralSeatLabel, instructionLanguageLabel } from "../seatLabel";
import DoctoralDateField from "../Partials/DoctoralDateField";

const STATUS_COLORS = {
  1: "default",
  2: "info",
  3: "primary",
  4: "success",
  5: "error",
};

const doctoralSeatFilters = [
  <SelectInput
    label="Статус"
    source="status"
    choices={DoctoralSeatStatusChoices}
    style={{ width: 300 }}
    alwaysOn
    emptyText="Всички"
  />,
];

const renderStatusChip = record => {
  const choice = DoctoralSeatStatusChoices.find(c => c.id === record.status);
  return (
    <Chip
      label={choice?.name ?? record.status}
      color={STATUS_COLORS[record.status] ?? "default"}
      size="small"
    />
  );
};

const ListDoctoralSeats = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const userPermissions = getPermissions();

  const ListActions = hasCreate => {
    return <TopToolbar>{hasCreate && <CreateButton />}</TopToolbar>;
  };

  return (
    <List
      title="Докторантски места"
      filters={doctoralSeatFilters}
      actions={<ListActions hasCreate={checkPermission(userPermissions, permissions.canCreateDoctoralSeats)} />}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <MobileListRow fields={[{ label: "Програма", value: doctoralSeatLabel(record) }]} />
          )}
          secondaryText={record => (
            <div>
              <MobileListRow
                fields={[
                  {
                    label: "Статус",
                    value: DoctoralSeatStatusChoices.find(c => c.id === record.status)?.name,
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Професионално направление",
                    value: (
                      <ReferenceField
                        source="professionalDirectionId"
                        reference={administrationRequests.getProfessionalDirectionsSelect()}
                        link={false}
                        record={record}
                      >
                        <TextField source="nameBg" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Изпит (дисциплина)",
                    value: (
                      <ReferenceField
                        source="doctoralDisciplineId"
                        reference={doctoralRequests.getDoctoralDisciplinesSelect()}
                        link={false}
                        record={record}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Брой места", value: record.seatCount }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Език",
                    value: instructionLanguageLabel(record.instructionLanguageId),
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Година", value: record.year }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Създадено на",
                    value: <DoctoralDateField source="createdOn" record={record} />,
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={record => (
            <>
              {record?.status !== DoctoralSeatStatus.ApprovedByMon && (
                <SecuredComponent permission={permissions.canUpdateDoctoralSeats}>
                  <EditButton label="" />
                </SecuredComponent>
              )}
              <ShowButton label="" />
            </>
          )}
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick={false}>
          <ReferenceField
            label="Докторантска програма"
            source="doctoralProgramId"
            reference={doctoralRequests.getDoctoralProgramsSelect()}
            link={false}
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            label="Професионално направление"
            source="professionalDirectionId"
            reference={administrationRequests.getProfessionalDirectionsSelect()}
            link={false}
            sortable={false}
          >
            <TextField source="nameBg" />
          </ReferenceField>
          <ReferenceField
            label="Форма на обучение"
            source="studyTypeId"
            reference={doctoralRequests.getDoctoralStudyTypesSelect()}
            link={false}
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            label="Изпит (дисциплина)"
            source="doctoralDisciplineId"
            reference={doctoralRequests.getDoctoralDisciplinesSelect()}
            link={false}
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <NumberField source="seatCount" label="Брой места" sortable={false} />
          <FunctionField
            label="Език на обучение"
            render={record => instructionLanguageLabel(record.instructionLanguageId)}
          />
          <NumberField source="year" label="Година" sortable={false} options={{ useGrouping: false }} />
          <FunctionField label="Статус" render={renderStatusChip} />
          <DoctoralDateField source="createdOn" label="Създадено на" sortable={false} />
          <FunctionField
            label=""
            render={record =>
              record?.status !== DoctoralSeatStatus.ApprovedByMon ? (
                <SecuredComponent permission={permissions.canUpdateDoctoralSeats}>
                  <EditButton />
                </SecuredComponent>
              ) : null
            }
          />
          <ShowButton />
        </Datagrid>
      )}
    </List>
  );
};

export default ListDoctoralSeats;
